'use client';

import { ContextDataEntry } from '@/@studio-ai/types';
import { Icon } from '@iconify/react';
import { useState } from 'react';

const SKIP_KEYS = new Set(['displayMessage', 'routingHints']);
const KNOWN_ABBREVIATIONS = new Set(['id', 'ids', 'url', 'urls', 'api']);
const PRIMITIVE_PREVIEW_COUNT = 3;

function formatKey(key: string): string {
    return key
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .split(' ')
        .map((word) => {
            const lower = word.toLowerCase();
            if (KNOWN_ABBREVIATIONS.has(lower)) {
                return lower === 'ids' ? 'IDs' : word.toUpperCase();
            }
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
}

function isEmpty(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
}

export function hasContextContent(contextEntry: ContextDataEntry): boolean {
    const { pagePath, pageData, selectedText, attachedDocuments } = contextEntry;
    return !!(
        pagePath ||
        selectedText ||
        (pageData && !isEmpty(pageData)) ||
        (attachedDocuments && attachedDocuments.length > 0)
    );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ─── Value Viewer ────────────────────────────────────────────────────────────

interface ValueViewerProps {
    value: unknown;
    depth?: number;
}

function ValueViewer({ value, depth = 0 }: ValueViewerProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showAll, setShowAll] = useState(false);

    if (isEmpty(value)) return null;

    // Primitive
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return <span className="text-mini text-default-700">{String(value)}</span>;
    }

    // Array
    if (Array.isArray(value)) {
        const hasObjects = value.some(isPlainObject);

        if (hasObjects) {
            return (
                <div>
                    <button
                        type="button"
                        className="flex items-center gap-0.5 text-mini text-primary hover:text-primary-400"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        <span>
                            {value.length} {value.length === 1 ? 'item' : 'items'}
                        </span>
                        <Icon
                            icon={isExpanded ? 'lucide:chevron-up' : 'lucide:chevron-down'}
                            width={12}
                            height={12}
                        />
                    </button>
                    {isExpanded && (
                        <div
                            className={`mt-1.5 space-y-2 ${depth > 0 ? 'ml-2 border-l border-default-200 pl-2' : ''}`}
                        >
                            {value.map((item, i) => (
                                <div key={i} className="rounded bg-default-100 p-2">
                                    <ObjectViewer
                                        data={item as Record<string, unknown>}
                                        depth={depth + 1}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        // Array of primitives
        const preview = value.slice(0, PRIMITIVE_PREVIEW_COUNT);
        const rest = value.slice(PRIMITIVE_PREVIEW_COUNT);
        const displayed = showAll ? value : preview;

        return (
            <span className="text-mini text-default-700">
                {displayed.map(String).join(', ')}
                {!showAll && rest.length > 0 && (
                    <>
                        {' '}
                        <button
                            type="button"
                            className="text-primary hover:text-primary-400"
                            onClick={() => setShowAll(true)}
                        >
                            +{rest.length} more
                        </button>
                    </>
                )}
                {showAll && rest.length > 0 && (
                    <>
                        {' '}
                        <button
                            type="button"
                            className="text-default-400 hover:text-default-600"
                            onClick={() => setShowAll(false)}
                        >
                            show less
                        </button>
                    </>
                )}
            </span>
        );
    }

    // Nested object
    if (isPlainObject(value)) {
        const visibleEntries = Object.entries(value).filter(([, v]) => !isEmpty(v));
        if (visibleEntries.length === 0) return null;

        return (
            <div>
                <button
                    type="button"
                    className="flex items-center gap-0.5 text-mini text-primary hover:text-primary-400"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <span>
                        {visibleEntries.length} {visibleEntries.length === 1 ? 'field' : 'fields'}
                    </span>
                    <Icon
                        icon={isExpanded ? 'lucide:chevron-up' : 'lucide:chevron-down'}
                        width={12}
                        height={12}
                    />
                </button>
                {isExpanded && (
                    <div
                        className={`mt-1.5 ${depth > 0 ? 'ml-2 border-l border-default-200 pl-2' : ''}`}
                    >
                        <ObjectViewer data={value} depth={depth + 1} />
                    </div>
                )}
            </div>
        );
    }

    return null;
}

// ─── Object Viewer ────────────────────────────────────────────────────────────

interface ObjectViewerProps {
    data: Record<string, unknown>;
    depth?: number;
    skipKeys?: Set<string>;
}

function ObjectViewer({ data, depth = 0, skipKeys }: ObjectViewerProps) {
    const entries = Object.entries(data).filter(([key, value]) => {
        if (skipKeys?.has(key)) return false;
        return !isEmpty(value);
    });

    if (entries.length === 0) return null;

    return (
        <div className="space-y-1.5">
            {entries.map(([key, value]) => (
                <div key={key} className="flex items-start gap-1">
                    <span className="shrink-0 text-mini font-medium text-default-500">
                        {formatKey(key)}:
                    </span>
                    <ValueViewer value={value} depth={depth} />
                </div>
            ))}
        </div>
    );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

interface ContextDataViewerProps {
    contextEntry: ContextDataEntry;
}

export function ContextDataViewer({ contextEntry }: ContextDataViewerProps) {
    const { pagePath, pageData, selectedText, attachedDocuments } = contextEntry;

    const hasContent =
        pagePath ||
        selectedText ||
        (pageData && !isEmpty(pageData)) ||
        (attachedDocuments && attachedDocuments.length > 0);

    if (!hasContent) return null;

    return (
        <div className="space-y-2">
            <div className="text-small font-medium text-default-600">Context</div>

            {pagePath && (
                <div className="flex items-start gap-1">
                    <span className="shrink-0 text-mini font-medium text-default-500">Page:</span>
                    <a
                        href={pagePath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-mini text-primary hover:underline"
                    >
                        {pagePath}
                    </a>
                </div>
            )}

            {selectedText && (
                <div className="flex items-start gap-1">
                    <span className="shrink-0 text-mini font-medium text-default-500">
                        Selected Text:
                    </span>
                    <blockquote className="border-l-2 border-default-300 pl-2 text-mini italic text-default-600">
                        {selectedText}
                    </blockquote>
                </div>
            )}

            {pageData && !isEmpty(pageData) && (
                <ObjectViewer data={pageData} depth={0} skipKeys={SKIP_KEYS} />
            )}

            {attachedDocuments && attachedDocuments.length > 0 && (
                <div className="flex items-start gap-1">
                    <span className="shrink-0 text-mini font-medium text-default-500">
                        Attached Documents:
                    </span>
                    <ValueViewer value={attachedDocuments} depth={0} />
                </div>
            )}
        </div>
    );
}
